/**
 * Pure helpers for the leaderboard admin screens (views/leaderboardAdmin.ts): turning the form
 * into a request, the rules blob a period carries, and the formatting/ordering of the review
 * list. No DOM, so it is all testable directly.
 */

import type { ScoringMode } from '../rules/hkos';
import type {
  CreateLeaderboardPeriodInput,
  LeaderboardPeriod,
  LeaderboardPeriodStatus,
  ReviewFlag,
  ReviewGameItem,
  Settlement,
} from './adminApi';
import { defaultHouseRules, type HouseRulesOverrides } from './views/settings';

/**
 * The venue picks a settlement style and that ALONE decides which HKOS mode's house rules the period
 * carries: Serious-style is zero-sum, Fun-style is winner-only (CLAUDE.md rule 3). Deriving one from
 * the other means a period can never declare one style and ship rules for the other.
 */
export const SETTLEMENT_MODE: Record<Settlement, ScoringMode> = {
  zero_sum: 'serious',
  winner_only: 'fun',
};

export const SETTLEMENT_LABEL: Record<Settlement, string> = {
  winner_only: 'Winner-only (Fun-style)',
  zero_sum: 'Zero-sum (Serious-style)',
};

export const SETTLEMENT_HELP: Record<Settlement, string> = {
  winner_only: 'Only round winners gain points and nobody ever goes negative. Rewards winning often.',
  zero_sum: 'Every game adds up to zero: winners gain what losers lose. Losers can go negative.',
};

/** The frozen house rules stored on a period: the settings a leaderboard game will be played under. */
export interface PeriodRules extends HouseRulesOverrides {
  mode: ScoringMode;
}

export function periodRulesBlob(settlement: Settlement, rules: HouseRulesOverrides): PeriodRules {
  return { mode: SETTLEMENT_MODE[settlement], toggles: rules.toggles, serious: rules.serious, fun: rules.fun };
}

/** Everything the "new period" form holds, as the strings the inputs hold — validated only on submit. */
export interface PeriodDraft {
  venue: string;
  /** A `datetime-local` value: local wall-clock time, no timezone. */
  startsAt: string;
  days: string;
  settlement: Settlement;
  minRounds: string;
  maxGamesPerDay: string;
  /** Blank = no period-wide limit. */
  maxGamesPerPeriod: string;
  minSecondsPerRound: string;
  rules: HouseRulesOverrides;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A `datetime-local` string for a moment, in the device's own timezone (what the input shows and edits). */
export function epochToLocalInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The moment a `datetime-local` value means in the device's own timezone, or undefined if it isn't a date. */
export function localInputToEpoch(value: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return undefined;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

/** A fresh form: starting at the next whole hour, 14 days long, winner-only, with the defaults of the app itself. */
export function defaultPeriodDraft(now: number, venue: string): PeriodDraft {
  const nextHour = Math.ceil((now + 1) / 3_600_000) * 3_600_000;
  return {
    venue,
    startsAt: epochToLocalInput(nextHour),
    days: '14',
    settlement: 'winner_only',
    minRounds: '8',
    maxGamesPerDay: '8',
    maxGamesPerPeriod: '',
    minSecondsPerRound: '60',
    rules: defaultHouseRules(),
  };
}

function wholeNumber(raw: string, label: string, min: number, max: number): { value: number } | { error: string } {
  const text = raw.trim();
  const n = Number(text);
  if (text === '' || !Number.isInteger(n) || n < min || n > max) {
    return { error: `${label} must be a whole number from ${min} to ${max}.` };
  }
  return { value: n };
}

export function buildPeriodRequest(draft: PeriodDraft): { ok: true; input: CreateLeaderboardPeriodInput } | { ok: false; error: string } {
  if (draft.venue.trim() === '') return { ok: false, error: 'Choose which venue this leaderboard is for.' };
  const startsAt = localInputToEpoch(draft.startsAt);
  if (startsAt === undefined) return { ok: false, error: 'Choose a start date and time.' };

  const days = wholeNumber(draft.days, 'Length in days', 1, 90);
  const minRounds = wholeNumber(draft.minRounds, 'Minimum rounds per game', 1, 200);
  const perDay = wholeNumber(draft.maxGamesPerDay, 'Games per player per day', 1, 50);
  const seconds = wholeNumber(draft.minSecondsPerRound, 'Minimum seconds per round', 0, 600);
  const perPeriod = draft.maxGamesPerPeriod.trim() === '' ? undefined : wholeNumber(draft.maxGamesPerPeriod, 'Games per player for the whole period', 1, 1000);
  for (const r of [days, minRounds, perDay, seconds, perPeriod]) {
    if (r && 'error' in r) return { ok: false, error: r.error };
  }

  return {
    ok: true,
    input: {
      venue: draft.venue.trim(),
      startsAt,
      days: (days as { value: number }).value,
      minRounds: (minRounds as { value: number }).value,
      settlement: draft.settlement,
      maxGamesPerDay: (perDay as { value: number }).value,
      ...(perPeriod ? { maxGamesPerPeriod: (perPeriod as { value: number }).value } : {}),
      minSecondsPerRound: (seconds as { value: number }).value,
      rules: periodRulesBlob(draft.settlement, draft.rules),
    },
  };
}

// --- display ---------------------------------------------------------------

export function formatDuration(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export function formatDayMonth(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** "1 Sep → 15 Sep" for a period; the end date shown is the last day it is open, not the instant after. */
export function formatPeriodRange(period: Pick<LeaderboardPeriod, 'startsAt' | 'endsAt'>): string {
  return `${formatDayMonth(period.startsAt)} → ${formatDayMonth(period.endsAt - 1)}`;
}

/** "25 Sep, 14:00" — a moment with its time, in the device's own timezone (24-hour, so there is no am/pm to misread). */
export function formatDayMonthTime(ms: number): string {
  const time = new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${formatDayMonth(ms)}, ${time}`;
}

/**
 * A period with its exact start and end times, and — while it has not started — how long until it does. The start time is what
 * decides whether players can open the leaderboard, so it must be visible: a period created "for today" that starts at the next
 * whole hour is not open yet, and a date alone (see `formatPeriodRange`) cannot show that.
 */
export function formatPeriodTimes(period: Pick<LeaderboardPeriod, 'startsAt' | 'endsAt'>, now: number): string {
  const range = `${formatDayMonthTime(period.startsAt)} → ${formatDayMonthTime(period.endsAt)}`;
  return period.startsAt > now ? `${range} · starts in ${formatDuration(period.startsAt - now)}` : range;
}

/** When a table was sent to the staff: the time, and how long ago ("26 Sep, 19:42 · 3 min ago"). */
export function formatTableWhen(requestedAt: number, now: number): string {
  return `${formatDayMonthTime(requestedAt)} · ${formatWaiting(requestedAt, now)}`;
}

export const STATUS_LABEL: Record<LeaderboardPeriodStatus, string> = {
  upcoming: 'Not started',
  running: 'Running',
  ended: 'Ended',
};

/** One line summarising a period's limits, e.g. "8 games a day · 30 for the period · 60 s per round minimum". */
export function describeLimits(p: Pick<LeaderboardPeriod, 'maxGamesPerDay' | 'maxGamesPerPeriod' | 'minSecondsPerRound' | 'minRounds'>): string {
  const parts = [`${p.maxGamesPerDay} ${p.maxGamesPerDay === 1 ? 'game' : 'games'} a day`];
  if (p.maxGamesPerPeriod !== null) parts.push(`${p.maxGamesPerPeriod} for the period`);
  parts.push(`at least ${p.minRounds} ${p.minRounds === 1 ? 'round' : 'rounds'}`);
  parts.push(p.minSecondsPerRound > 0 ? `${p.minSecondsPerRound} s per round minimum` : 'no minimum time');
  return parts.join(' · ');
}

export function warnCount(flags: ReviewFlag[]): number {
  return flags.filter((f) => f.severity === 'warn').length;
}

/**
 * Review order: games with warnings first (most warnings first), then everything else, each group newest
 * first; hidden games always sink to the bottom, since they have already been dealt with.
 */
export function sortReviewGames(games: ReviewGameItem[]): ReviewGameItem[] {
  return [...games].sort((a, b) => {
    if (a.hidden !== b.hidden) return a.hidden ? 1 : -1;
    const wa = warnCount(a.flags);
    const wb = warnCount(b.flags);
    if (wa !== wb) return wb - wa;
    const fa = a.flags.length;
    const fb = b.flags.length;
    if (fa !== fb) return fb - fa;
    return b.completedAt - a.completedAt;
  });
}

export function validatePinInput(pin: string): string | undefined {
  return /^\d{4}$/.test(pin.trim()) ? undefined : 'A PIN is exactly 4 digits.';
}

/** "just now", "5 min ago", "3 h ago", "2 days ago" — how long a join request has been waiting. */
export function formatWaiting(requestedAt: number, now: number): string {
  const minutes = Math.floor(Math.max(0, now - requestedAt) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
